import { useMemo, type PointerEvent } from 'react';
import type { TelemetryReading } from '../../hooks/useTelemetry.ts';
import { Panel } from '../Panel.tsx';
import { Empty } from '../Empty.tsx';
import { midEllipsis, NO_VALUE } from '../format.ts';
import { FaultLine } from '../states/Faults.tsx';
import { Figure } from '../states/Figure.tsx';
import { unread, type Read } from '../states/read.ts';
import { stampOf, type TimeAxis } from './channels.ts';
import { useCursor } from './cursor.ts';
import { nearestIndex } from './scale.ts';
import type { ChartSeries } from './TimeSeriesCard.tsx';
import { useMeasure } from './useMeasure.ts';
import styles from './charts.module.css';

/* ==========================================================================
   Ground track — the same window, drawn in metres instead of seconds.

   `auterion-flight-log-review.png` pairs the route with a plot of the same
   run so the operator reads "where" and "what" together; this is that pairing
   with the console's own cursor between them. Hovering the track moves the
   shared cursor to the fix under the pointer, so every trace in the grid jumps
   to the moment the robot was standing there, and vice versa.

   Projection is a local ENU tangent plane anchored on the FIRST fix in the
   window — metres, equal aspect, with a scale bar. It is not a map and does
   not pretend to be one: no basemap, no north-up claim beyond the drawn N,
   and the anchor's own WGS84 coordinate is printed so the frame is nameable
   (§13 tell 14 — a telemetry number without its reference frame).
   ========================================================================== */

const M_PER_DEG_LAT = 111_320;

interface Fix {
  t: number;
  e: number;
  n: number;
  heading: number | null;
}

function projectFixes(
  readings: readonly TelemetryReading[],
  axis: TimeAxis,
  from: number,
  to: number,
): { fixes: Fix[]; anchor: { lat: number; lon: number } | null } {
  const rows: TelemetryReading[] = [];
  for (let i = readings.length - 1; i >= 0; i -= 1) {
    const r = readings[i]!;
    const ts = stampOf(r, axis);
    if (!Number.isFinite(ts) || ts < from || ts > to) continue;
    if (r.lat == null || r.lon == null) continue;
    rows.push(r);
  }
  const first = rows[0];
  if (first === undefined || first.lat == null || first.lon == null) {
    return { fixes: [], anchor: null };
  }
  const lat0 = first.lat;
  const lon0 = first.lon;
  const mPerDegLon = M_PER_DEG_LAT * Math.cos((lat0 * Math.PI) / 180);
  const fixes = rows.map((r) => ({
    t: stampOf(r, axis),
    e: ((r.lon as number) - lon0) * mPerDegLon,
    n: ((r.lat as number) - lat0) * M_PER_DEG_LAT,
    heading: r.heading_deg,
  }));
  return { fixes, anchor: { lat: lat0, lon: lon0 } };
}

/** 1 · 2 · 5 · 10 … metres, whichever is closest to a quarter of the view. */
function scaleBarMetres(target: number): number {
  if (target <= 0) return 1;
  const mag = 10 ** Math.floor(Math.log10(target));
  const n = target / mag;
  return (n < 1.5 ? 1 : n < 3.5 ? 2 : n < 7.5 ? 5 : 10) * mag;
}

export function TrackCard({
  series,
  axis,
  read,
}: {
  series: readonly ChartSeries[];
  axis: TimeAxis;
  /**
   * THE READ THE FIXES CAME FROM. Required: `0 m · 0 fixes` and `No position
   * in this window` are both counted from `series`, and a robot that drove all
   * morning produces exactly the same two figures as an endpoint that never
   * answered. The panel also owed a fault line and had none.
   */
  read: Read;
}) {
  const cursor = useCursor();
  const tracks = useMemo(
    () =>
      series.map((s) => ({
        id: s.id,
        colour: s.colour,
        ...projectFixes(s.readings, axis, cursor.from, cursor.to),
      })),
    [series, axis, cursor.from, cursor.to],
  );
  const total = tracks.reduce((n, t) => n + t.fixes.length, 0);

  /* Path length, summed over the fixes actually in the window — the one number
     a route drawing cannot be read off by eye, and the reason the scale bar is
     not enough on its own. Straight-line between fixes, which at a 2m24s
     cadence is a floor on the distance driven, not an estimate of it. */
  const metres = useMemo(
    () =>
      tracks.reduce((sum, t) => {
        let d = 0;
        for (let i = 1; i < t.fixes.length; i += 1) {
          const a = t.fixes[i - 1]!;
          const b = t.fixes[i]!;
          d += Math.hypot(b.e - a.e, b.n - a.n);
        }
        return sum + d;
      }, 0),
    [tracks],
  );

  return (
    <Panel
      title="Ground track"
      {...(series.length > 1 ? { count: series.length } : {})}
      meta={
        <span className={styles.cardMeta}>
          <span
            className={styles.metaRange}
            title="Straight-line distance between consecutive fixes — a floor on the distance driven."
          >
            <Figure
              of={read}
              value={metres >= 1000 ? `${(metres / 1000).toFixed(2)} km` : `${metres.toFixed(0)} m`}
            />
          </span>
          <span className={styles.metaRange}><Figure of={read} value={total} /></span>
          <span className={styles.metaUnit}>fixes</span>
          {/* NORTH, STATED ONCE WHEN THE PANEL IS SMALL MULTIPLES. Every cell
              is the same construction — a local ENU tangent plane, grid north
              up — so one statement is true of all of them, and a per-cell
              arrow inside a 90px cell only takes the width the robot's id
              needs. A single track keeps its arrow on the plot, where map
              furniture belongs. */}
          {tracks.length > 1 && (
            <span
              className={styles.metaUnit}
              title="Every cell is a local ENU tangent plane with grid north up."
            >
              ↑ N
            </span>
          )}
        </span>
      }
      pad="none"
      scroll={false}
      className={styles.card}
    >
      {/* The fault line the panel owed. Flush above the absence line, so the
          two answer their two questions in the order every other panel on the
          surface answers them: WHY there is nothing, then WHAT is there. */}
      {unread(read) && <FaultLine read={read} />}
      {total === 0 ? (
        <Empty of={read} detail="lat/lon · no fixes in window">No position in this window</Empty>
      ) : (
        <div className={styles.trackRow}>
          {tracks.map((t) => (
            <Track key={t.id} {...t} named={tracks.length > 1} />
          ))}
        </div>
      )}
    </Panel>
  );
}

function Track({
  id,
  colour,
  fixes,
  anchor,
  named,
}: {
  id: string;
  colour: string;
  fixes: readonly Fix[];
  anchor: { lat: number; lon: number } | null;
  /** Print the robot's name on the plot — only worth the line when there are
      two tracks to tell apart. The frame caption is always worth it. */
  named: boolean;
}) {
  const cursor = useCursor();
  const [ref, box] = useMeasure<HTMLDivElement>();
  const w = box.w;
  const h = box.h;

  const geom = useMemo(() => {
    if (fixes.length === 0 || w < 8 || h < 8) return null;
    let e0 = Infinity;
    let e1 = -Infinity;
    let n0 = Infinity;
    let n1 = -Infinity;
    for (const f of fixes) {
      if (f.e < e0) e0 = f.e;
      if (f.e > e1) e1 = f.e;
      if (f.n < n0) n0 = f.n;
      if (f.n > n1) n1 = f.n;
    }
    const pad = 14;
    const spanE = Math.max(e1 - e0, 1);
    const spanN = Math.max(n1 - n0, 1);
    /* Equal aspect, always: a track squashed to fill its box is a lie about
       the shape of the route the robot drove. */
    const scale = Math.min((w - pad * 2) / spanE, (h - pad * 2) / spanN);
    const cx = (e0 + e1) / 2;
    const cy = (n0 + n1) / 2;
    const px = (e: number) => w / 2 + (e - cx) * scale;
    const py = (n: number) => h / 2 - (n - cy) * scale;
    return { px, py, scale, spanE, spanN };
  }, [fixes, w, h]);

  const pts = useMemo(() => {
    if (geom === null) return '';
    return fixes
      .map((f, i) => `${i === 0 ? 'M' : 'L'}${geom.px(f.e).toFixed(1)},${geom.py(f.n).toFixed(1)}`)
      .join('');
  }, [fixes, geom]);

  const times = useMemo(() => fixes.map((f) => f.t), [fixes]);
  const i = cursor.at === null ? -1 : nearestIndex(times, cursor.at);
  const cur = i >= 0 ? fixes[i] : undefined;

  const onMove = (e: PointerEvent<HTMLDivElement>) => {
    if (geom === null) return;
    const r = e.currentTarget.getBoundingClientRect();
    const mx = e.clientX - r.left;
    const my = e.clientY - r.top;
    let best = -1;
    let bestD = Infinity;
    for (let k = 0; k < fixes.length; k += 1) {
      const f = fixes[k]!;
      const dx = geom.px(f.e) - mx;
      const dy = geom.py(f.n) - my;
      const d = dx * dx + dy * dy;
      if (d < bestD) {
        bestD = d;
        best = k;
      }
    }
    if (best >= 0 && bestD < 40 * 40) cursor.setHover(fixes[best]!.t);
  };

  const bar = geom === null ? null : scaleBarMetres((geom.spanE * 0.28) || 1);
  const barPx = geom === null || bar === null ? 0 : bar * geom.scale;

  return (
    <div
      className={styles.track}
      ref={ref}
      onPointerMove={onMove}
      onPointerLeave={() => cursor.setHover(null)}
      role="img"
      title={
        anchor === null
          ? id
          : `${id} — local ENU metres from ${anchor.lat.toFixed(5)}, ${anchor.lon.toFixed(5)}`
      }
      aria-label={`Ground track for ${id}, ${fixes.length} fixes, local ENU metres${
        anchor === null ? '' : ` from ${anchor.lat.toFixed(5)}, ${anchor.lon.toFixed(5)}`
      }`}
    >
      {geom !== null && (
        <svg width={w} height={h} aria-hidden="true">
          <path d={pts} fill="none" stroke={colour} strokeWidth={1} strokeLinejoin="round" />
          {/* Where the window starts and where it ends: hollow for the first
              fix, filled for the last, so the direction of travel is readable
              from the shape alone. The cursor's own fix is the accent dot. */}
          {fixes[0] !== undefined && (
            <circle
              cx={geom.px(fixes[0].e)} cy={geom.py(fixes[0].n)} r={2.5}
              fill="none" stroke="var(--text-low)" strokeWidth={1}
            />
          )}
          {fixes.length > 1 && fixes[fixes.length - 1] !== undefined && (
            <rect
              x={geom.px(fixes[fixes.length - 1]!.e) - 2}
              y={geom.py(fixes[fixes.length - 1]!.n) - 2}
              width={4} height={4}
              fill="var(--text-mid)"
              shapeRendering="crispEdges"
            />
          )}
          {/* North, drawn rather than assumed — the projection is a local ENU
              tangent plane and its up is grid north, which is worth stating
              on a plot that has no basemap to infer it from. */}
          {!named && (
            <g className={styles.northMark}>
              <line
                x1={w - 11} y1={16} x2={w - 11} y2={5}
                stroke="var(--text-dis)" strokeWidth={1}
              />
              <path d={`M${w - 11},4 l-2.5,4 l5,0 z`} fill="var(--text-dis)" />
              <text className={styles.axisLabel} x={w - 8} y={16}>N</text>
            </g>
          )}
          {cur !== undefined && (
            <>
              <circle cx={geom.px(cur.e)} cy={geom.py(cur.n)} r={3} fill="var(--accent)" />
              {cur.heading != null && (
                <line
                  x1={geom.px(cur.e)}
                  y1={geom.py(cur.n)}
                  x2={geom.px(cur.e) + Math.sin((cur.heading * Math.PI) / 180) * 12}
                  y2={geom.py(cur.n) - Math.cos((cur.heading * Math.PI) / 180) * 12}
                  stroke="var(--accent)"
                  strokeWidth={1}
                />
              )}
            </>
          )}
          {barPx > 8 && (
            <>
              <line
                x1={8} y1={h - 8} x2={8 + barPx} y2={h - 8}
                stroke="var(--text-low)" strokeWidth={1} shapeRendering="crispEdges"
              />
              <line x1={8} y1={h - 11} x2={8} y2={h - 5} stroke="var(--text-low)" strokeWidth={1} />
              <line
                x1={8 + barPx} y1={h - 11} x2={8 + barPx} y2={h - 5}
                stroke="var(--text-low)" strokeWidth={1}
              />
              <text className={styles.axisLabel} x={12 + barPx} y={h - 5}>{`${bar} m`}</text>
            </>
          )}
        </svg>
      )}
      <div className={styles.trackHead}>
        {named && (
          /* MIDDLE ellipsis, never end (§5): a fleet is `jetrover-01` …
             `jetrover-08` and the discriminating characters are the last
             three, so `jetrover…` names eight machines and `jetro…-01` names
             one. 6.4px is the mono advance at this size; 20px clears the
             cell has to itself once the north mark moves to the panel bar. */
          <span
            className={styles.trackId}
            style={{ borderColor: colour }}
            title={id}
          >
            {midEllipsis(id, Math.max(5, Math.floor((w - 12) / 6.6)))}
          </span>
        )}
        {/* The frame is the point of the caption, but a truncated coordinate is
            worse than none — below ~220px it drops and the accessible name and
            the title keep carrying it. */}
        {(w >= 220 || !named) && (
          <span className={styles.trackFrame}>
            {anchor === null
              ? NO_VALUE
              : `ENU from ${anchor.lat.toFixed(5)}, ${anchor.lon.toFixed(5)}`}
          </span>
        )}
      </div>
    </div>
  );
}
